import { create } from 'zustand'
import { cardsApi } from '../api/cardsApi'
import type {
  Card,
  CardsListParams,
  CreateCardData,
  UpdateCardData,
} from '../types'

export interface CardsFilters {
  progress_min?: number
  progress_max?: number
  search?: string
  part_of_speech?: string
  type?: 'word' | 'phrase'
  tags?: number[]
  dictionary_id?: number
  without_translation?: boolean
  without_transcription?: boolean
  without_audio?: boolean
}

export interface CardsSorting {
  order_by?: 'created_at' | 'updated_at' | 'progress'
  order_direction?: 'asc' | 'desc'
}

interface CardsState {
  cards: Card[]
  currentCard: Card | null
  isLoading: boolean
  error: string | null
  pagination: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
  filters: CardsFilters
  sorting: CardsSorting

  loadCards: (params?: CardsListParams) => Promise<void>
  fetchCard: (id: number) => Promise<Card | null>
  createCard: (data: CreateCardData) => Promise<Card>
  updateCard: (id: number, data: UpdateCardData) => Promise<Card>
  deleteCard: (id: number) => Promise<void>
  setFilters: (filters: Partial<CardsFilters>) => void
  setSorting: (sorting: Partial<CardsSorting>) => void
  reset: () => void
  clearError: () => void
}

const initialPagination = {
  current_page: 1,
  last_page: 1,
  per_page: 15,
  total: 0,
}

const initialState = {
  cards: [],
  currentCard: null,
  isLoading: false,
  error: null,
  pagination: initialPagination,
  filters: {} as CardsFilters,
  sorting: {} as CardsSorting,
}

export const useCardsStore = create<CardsState>((set, get) => ({
  ...initialState,

  loadCards: async (params) => {
    set({ isLoading: true, error: null })
    try {
      const { filters, sorting, pagination } = get()
      const merged: CardsListParams = {
        ...filters,
        ...sorting,
        page: params?.page ?? pagination.current_page,
        per_page: params?.per_page ?? pagination.per_page,
        ...params,
      }
      const response = await cardsApi.getCards(merged)
      set({
        cards: response.data,
        pagination: {
          current_page: response.meta.current_page,
          last_page: response.meta.last_page,
          per_page: response.meta.per_page,
          total: response.meta.total,
        },
      })
    } catch (error) {
      const e = error as { response?: { data?: { message?: string } }; message?: string }
      const message =
        e.response?.data?.message || e.message || 'Ошибка загрузки карточек'
      set({ error: message })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  fetchCard: async (id) => {
    set({ isLoading: true, error: null })
    try {
      const card = await cardsApi.getCard(id)
      set({ currentCard: card })
      return card
    } catch (error) {
      const e = error as { response?: { data?: { message?: string } }; message?: string }
      const message =
        e.response?.data?.message || e.message || 'Ошибка загрузки карточки'
      set({ error: message })
      return null
    } finally {
      set({ isLoading: false })
    }
  },

  createCard: async (data) => {
    set({ isLoading: true, error: null })
    try {
      const card = await cardsApi.createCard(data)
      set((state) => ({ cards: [card, ...state.cards] }))
      return card
    } catch (error) {
      const e = error as { response?: { data?: { message?: string } }; message?: string }
      const message =
        e.response?.data?.message || e.message || 'Ошибка создания карточки'
      set({ error: message })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  updateCard: async (id, data) => {
    set({ isLoading: true, error: null })
    try {
      const card = await cardsApi.updateCard(id, data)
      set((state) => ({
        cards: state.cards.map((c) => (c.id === id ? card : c)),
        currentCard: state.currentCard?.id === id ? card : state.currentCard,
      }))
      return card
    } catch (error) {
      const e = error as { response?: { data?: { message?: string } }; message?: string }
      const message =
        e.response?.data?.message || e.message || 'Ошибка обновления карточки'
      set({ error: message })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  deleteCard: async (id) => {
    set({ isLoading: true, error: null })
    try {
      await cardsApi.deleteCard(id)
      set((state) => ({
        cards: state.cards.filter((c) => c.id !== id),
        currentCard: state.currentCard?.id === id ? null : state.currentCard,
      }))
    } catch (error) {
      const e = error as { response?: { data?: { message?: string } }; message?: string }
      const message =
        e.response?.data?.message || e.message || 'Ошибка удаления карточки'
      set({ error: message })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  setFilters: (filters) =>
    set((state) => ({ filters: { ...state.filters, ...filters } })),

  setSorting: (sorting) =>
    set((state) => ({ sorting: { ...state.sorting, ...sorting } })),

  reset: () => set(initialState),

  clearError: () => set({ error: null }),
}))
